// src/pages/admin.js
// Brugeradministration – kun for admins. Samme designsprog som beregneren (ab-admin).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../auth';

const SURFACE = '#F7F8F7';
const SURFACE_SHADOW = '0 2px 8px rgba(16, 24, 40, 0.06)';
const BORDER = '#E5E5E5';
const MUTED = '#6B7280';
const GREEN = '#00B861';
const MIN_PASSWORD_LENGTH = 10;

const Page = styled.div`
  min-height: 100vh;
  padding: 12px;
  background: #ffffff;
`;

const Surface = styled.div`
  background: ${SURFACE};
  border-radius: 16px;
  box-shadow: ${SURFACE_SHADOW};
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
`;

const Logo = styled.img`
  height: 32px;
  display: block;
`;

const Title = styled.h1`
  color: #171717;
  font-size: 18px;
  font-weight: 700;
`;

const SectionTitle = styled.h2`
  color: #171717;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const SectionHint = styled.p`
  color: ${MUTED};
  font-size: 13px;
  margin-bottom: 16px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid ${BORDER};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
`;

const Grid = styled.form`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 2fr auto;
  gap: 12px;
  align-items: end;

  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
`;

const inputCss = `
  width: 100%;
  padding: 11px 14px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  transition: box-shadow 0.2s, border-color 0.2s;

  &::placeholder { color: #A3A3A3; }

  &:focus {
    outline: none;
    border-color: ${GREEN};
    box-shadow: 0 0 0 2px ${GREEN};
  }

  &:disabled {
    background: ${SURFACE};
    color: ${MUTED};
  }
`;

const Input = styled.input`${inputCss}`;
const Select = styled.select`${inputCss} padding-right: 32px;`;

const Button = styled.button`
  background: ${props => (props.$variant === 'secondary' ? '#ffffff' : props.$variant === 'danger' ? '#DC2626' : GREEN)};
  color: ${props => (props.$variant === 'secondary' ? '#171717' : '#ffffff')};
  border: ${props => (props.$variant === 'secondary' ? `1px solid ${BORDER}` : 'none')};
  min-height: ${props => (props.$small ? '34px' : '44px')};
  padding: 0 ${props => (props.$small ? '12px' : '18px')};
  border-radius: ${props => (props.$small ? '10px' : '12px')};
  font-size: ${props => (props.$small ? '13px' : '14px')};
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: ${props => (props.$variant === 'secondary' ? SURFACE : props.$variant === 'danger' ? '#B91C1C' : '#008f4c')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const PasswordRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${MUTED};
    padding: 0 12px 10px;
    border-bottom: 1px solid ${BORDER};
  }

  td {
    padding: 12px;
    border-bottom: 1px solid #F0F1F0;
    vertical-align: middle;
  }

  tr:last-child td { border-bottom: none; }

  td select { min-width: 96px; padding: 7px 30px 7px 10px; border-radius: 10px; }
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${GREEN};
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

const Badge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => (props.$tone === 'warn' ? '#FEF3C7' : '#ECFDF5')};
  color: ${props => (props.$tone === 'warn' ? '#92400E' : '#047857')};
`;

const Notice = styled.div`
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
  border: 1px solid ${props => (props.$tone === 'error' ? '#FECACA' : '#A7F3D0')};
  background: ${props => (props.$tone === 'error' ? '#FEF2F2' : '#ECFDF5')};
  color: ${props => (props.$tone === 'error' ? '#B91C1C' : '#065F46')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const Code = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  background: #ffffff;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  padding: 2px 8px;
`;

const linkStyle = { color: MUTED, fontSize: '13px', fontWeight: 500, textDecoration: 'underline' };

// Kode uden tegn der let forveksles (0/O, 1/l/I)
function generatePassword(length = 14) {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
}

const apiError = (err, fallback) => err.response?.data?.error || fallback;

const linkUrl = token => `${window.location.origin}/invite/${token}`;
const formatDate = ms => new Date(ms).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });

// "emil.hansen@…" → "Emil Hansen". Kan rettes i forhåndsvisningen.
function guessName(email) {
  const local = (email.split('@')[0] || '').replace(/\d+/g, ' ');
  return local
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
}

// Én person pr. linje: e-mail og initialer i vilkårlig rækkefølge, evt. navn.
// Adskilt af komma, semikolon eller tab.
function parseList(text) {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const parts = line.split(/[,;\t]/).map(p => p.trim()).filter(Boolean);
      const username = (parts.find(p => p.includes('@')) || '').toLowerCase();
      const initials = (parts.find(p => !p.includes('@') && /^[A-Za-zÆØÅæøå]{2,4}$/.test(p)) || '').toUpperCase();
      const name = parts.find(p => p !== username && p.toUpperCase() !== initials && !p.includes('@')) || guessName(username);
      return { username, initials, name, line };
    });
}

const EMPTY_FORM = { username: '', name: '', initials: '', role: 'user', password: '' };

const Textarea = styled.textarea`${inputCss}
  min-height: 120px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  resize: vertical;
`;

function Admin() {
  const { user: me, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [sheetInitials, setSheetInitials] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [notice, setNotice] = useState(null); // { tone, text, password? }
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetFor, setResetFor] = useState(null); // { username, password }
  const [bulkText, setBulkText] = useState('');
  const [bulkRows, setBulkRows] = useState(null);   // forhåndsvisning
  const [bulkResults, setBulkResults] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data.users);
      setSheetInitials(res.data.spreadsheetInitials);
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke hente brugere') });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const usedInitials = new Set(users.map(u => u.initials));

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const res = await axios.post('/api/admin/users', form);
      setNotice(res.data.link
        ? {
          tone: 'success',
          text: `${res.data.user.name} er inviteret. Send linket til ${res.data.user.username} (gyldigt til ${formatDate(res.data.link.expiresAt)}):`,
          secret: linkUrl(res.data.link.token),
          copyLabel: 'Kopiér link',
        }
        : {
          tone: 'success',
          text: `${res.data.user.name} er oprettet. Send e-mail og adgangskode til brugeren:`,
          secret: form.password,
          copyLabel: 'Kopiér kode',
        });
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke oprette bruger') });
    } finally {
      setBusy(false);
    }
  };

  const updateUser = async (username, changes, successText) => {
    setNotice(null);
    try {
      await axios.patch(`/api/admin/users/${encodeURIComponent(username)}`, changes);
      if (successText) setNotice({ tone: 'success', ...successText });
      load();
      return true;
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke opdatere bruger') });
      return false;
    }
  };

  const deleteUser = async (username) => {
    setNotice(null);
    try {
      await axios.delete(`/api/admin/users/${encodeURIComponent(username)}`);
      setConfirmDelete(null);
      setNotice({ tone: 'success', text: `${username} er slettet og logget ud.` });
      load();
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke slette bruger') });
    }
  };

  const saveReset = async () => {
    const ok = await updateUser(resetFor.username, { password: resetFor.password }, {
      text: `Ny adgangskode til ${resetFor.username} (brugeren er logget ud):`,
      secret: resetFor.password,
      copyLabel: 'Kopiér kode',
    });
    if (ok) setResetFor(null);
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  const makeLink = async (u) => {
    setNotice(null);
    try {
      const res = await axios.post(`/api/admin/users/${encodeURIComponent(u.username)}/link`);
      const { token, purpose, expiresAt } = res.data.link;
      setNotice({
        tone: 'success',
        text: `${purpose === 'reset' ? 'Nulstillingslink' : 'Invitationslink'} til ${u.username} (gyldigt til ${formatDate(expiresAt)}, et tidligere link virker ikke længere):`,
        secret: linkUrl(token),
        copyLabel: 'Kopiér link',
      });
      load();
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke lave link') });
    }
  };

  const previewBulk = () => {
    setBulkResults(null);
    setBulkRows(parseList(bulkText));
  };

  const rowProblem = (row, rows, index) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.username)) return 'Ingen gyldig e-mail';
    if (!row.initials) return 'Mangler initialer';
    if (!sheetInitials.includes(row.initials)) return 'Intet regneark til initialerne';
    if (users.some(u => u.username === row.username)) return 'Findes allerede';
    if (rows.findIndex(r => r.username === row.username) !== index) return 'Står allerede ovenfor';
    if (!row.name.trim()) return 'Mangler navn';
    return null;
  };

  const createBulk = async () => {
    const valid = bulkRows.filter((r, i) => !rowProblem(r, bulkRows, i));
    if (!valid.length) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await axios.post('/api/admin/users/bulk', {
        users: valid.map(({ username, name, initials }) => ({ username, name, initials })),
      });
      setBulkResults(res.data.results);
      setBulkRows(null);
      setBulkText('');
      load();
    } catch (err) {
      setNotice({ tone: 'error', text: apiError(err, 'Kunne ikke oprette brugere') });
    } finally {
      setBusy(false);
    }
  };

  const bulkCopyText = (bulkResults || [])
    .filter(r => r.link)
    .map(r => `${r.name} <${r.username}>\n${linkUrl(r.link.token)}`)
    .join('\n\n');

  return (
    <Page>
      <Surface>
        <Header>
          <Link to="/calculator"><Logo src="/Autobasen_logo.svg" alt="Autobasen Logo" /></Link>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Title>Brugere</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '13px' }}>
              <Avatar style={{ width: 26, height: 26 }}>{me.initials}</Avatar>
              <span style={{ fontWeight: 600 }}>{me.name}</span>
              <Link to="/calculator" style={linkStyle}>Til beregner</Link>
              <button onClick={logout} style={{ ...linkStyle, background: 'none', border: 'none', cursor: 'pointer' }}>
                Log ud
              </button>
            </div>
          </div>
        </Header>

        {notice && (
          <Notice $tone={notice.tone}>
            <span>
              {notice.text}
              {notice.secret && <> <Code style={{ wordBreak: 'break-all' }}>{notice.secret}</Code></>}
            </span>
            <span style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {notice.secret && (
                <Button $small $variant="secondary" onClick={() => copy(notice.secret)}>{notice.copyLabel}</Button>
              )}
              <Button $small $variant="secondary" onClick={() => setNotice(null)}>Luk</Button>
            </span>
          </Notice>
        )}

        <Card>
          <SectionTitle>Opret bruger</SectionTitle>
          <SectionHint>
            Initialerne bestemmer hvilket regneark brugeren regner i. Lad adgangskoden stå tom for at lave et
            invitationslink, så brugeren selv vælger kode.
          </SectionHint>
          <Grid onSubmit={createUser}>
            <Field>
              E-mail
              <Input
                type="email"
                required
                autoComplete="off"
                placeholder="navn@autobasen.dk"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
              />
            </Field>
            <Field>
              Navn
              <Input
                required
                placeholder="Fornavn"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field>
              Initialer
              <Select required value={form.initials} onChange={e => setForm({ ...form, initials: e.target.value })}>
                <option value="" disabled>Vælg</option>
                {sheetInitials.map(i => (
                  <option key={i} value={i}>{i}{usedInitials.has(i) ? ' (i brug)' : ''}</option>
                ))}
              </Select>
            </Field>
            <Field>
              Rolle
              <Select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="user">Bruger</option>
                <option value="admin">Admin</option>
              </Select>
            </Field>
            <Field>
              Adgangskode
              <PasswordRow>
                <Input
                  minLength={MIN_PASSWORD_LENGTH}
                  autoComplete="new-password"
                  placeholder="Tom = invitationslink"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
                <Button type="button" $variant="secondary" onClick={() => setForm({ ...form, password: generatePassword() })}>
                  Generér
                </Button>
              </PasswordRow>
            </Field>
            <Button type="submit" disabled={busy}>
              {busy ? 'Opretter…' : form.password ? 'Opret' : 'Invitér'}
            </Button>
          </Grid>
        </Card>

        <Card>
          <SectionTitle>Invitér flere</SectionTitle>
          <SectionHint>
            Én person pr. linje: e-mail og initialer, evt. navn – adskilt af komma. Navnet gættes ud fra e-mailen,
            hvis det mangler, og kan rettes inden oprettelse.
          </SectionHint>

          {!bulkRows && !bulkResults && (
            <>
              <Textarea
                placeholder={'emil.hansen@autobasen.dk, EE\nmohammed@autobasen.dk, MA, Mohammed'}
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
              />
              <div style={{ marginTop: '12px', textAlign: 'right' }}>
                <Button disabled={!bulkText.trim()} onClick={previewBulk}>Forhåndsvis</Button>
              </div>
            </>
          )}

          {bulkRows && (
            <>
              <Table>
                <thead>
                  <tr><th>E-mail</th><th>Navn</th><th>Initialer</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {bulkRows.map((row, i) => {
                    const problem = rowProblem(row, bulkRows, i);
                    return (
                      <tr key={i}>
                        <td style={{ color: row.username ? '#171717' : MUTED }}>{row.username || row.line}</td>
                        <td>
                          <Input
                            style={{ padding: '7px 10px', borderRadius: '10px' }}
                            value={row.name}
                            onChange={e => setBulkRows(bulkRows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                          />
                        </td>
                        <td>
                          <Select
                            value={row.initials}
                            onChange={e => setBulkRows(bulkRows.map((r, j) => (j === i ? { ...r, initials: e.target.value } : r)))}
                          >
                            {!sheetInitials.includes(row.initials) && <option value={row.initials}>{row.initials || '—'}</option>}
                            {sheetInitials.map(x => <option key={x} value={x}>{x}</option>)}
                          </Select>
                        </td>
                        <td>{problem ? <Badge $tone="warn">{problem}</Badge> : <Badge>Klar</Badge>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: MUTED, fontSize: '13px' }}>
                  {bulkRows.filter((r, i) => !rowProblem(r, bulkRows, i)).length} af {bulkRows.length} klar · rækker med fejl springes over
                </span>
                <Button $variant="secondary" onClick={() => setBulkRows(null)}>Tilbage</Button>
                <Button disabled={busy || !bulkRows.some((r, i) => !rowProblem(r, bulkRows, i))} onClick={createBulk}>
                  {busy ? 'Opretter…' : 'Opret og lav links'}
                </Button>
              </div>
            </>
          )}

          {bulkResults && (
            <>
              <Table>
                <thead>
                  <tr><th>Bruger</th><th>Link</th><th style={{ textAlign: 'right' }} /></tr>
                </thead>
                <tbody>
                  {bulkResults.map(r => (
                    <tr key={r.username}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.name || r.username}</div>
                        <div style={{ color: MUTED, fontSize: '13px' }}>{r.username}</div>
                      </td>
                      <td style={{ wordBreak: 'break-all' }}>
                        {r.link ? <Code>{linkUrl(r.link.token)}</Code> : <Badge $tone="warn">{r.error}</Badge>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {r.link && (
                          <Button $small $variant="secondary" onClick={() => copy(linkUrl(r.link.token))}>Kopiér</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: MUTED, fontSize: '13px' }}>
                  Linkene virker i 7 dage og vises kun her – kopiér dem nu.
                </span>
                <Button $variant="secondary" onClick={() => setBulkResults(null)}>Færdig</Button>
                <Button disabled={!bulkCopyText} onClick={() => copy(bulkCopyText)}>Kopiér alle</Button>
              </div>
            </>
          )}
        </Card>

        <Card style={{ marginBottom: 0 }}>
          <SectionTitle>Alle brugere</SectionTitle>
          <SectionHint>
            Ændringer gælder med det samme. Ny adgangskode, ændret rolle eller sletning logger brugeren ud.
            "Link" laver et engangslink: invitation hvis brugeren ikke har valgt kode endnu, ellers nulstilling.
          </SectionHint>
          <Table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>E-mail</th>
                <th>Initialer</th>
                <th>Rolle</th>
                <th style={{ textAlign: 'right' }}>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isMe = u.username === me.username;
                const hasSheet = sheetInitials.includes(u.initials);
                return (
                  <React.Fragment key={u.username}>
                    <tr>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                          <Avatar>{u.initials}</Avatar>
                          <span style={{ fontWeight: 600 }}>{u.name}</span>
                          {isMe && <Badge>Dig</Badge>}
                          {u.status === 'invited' && (
                            <Badge $tone="warn">
                              {u.linkExpiresAt ? `Inviteret · link til ${formatDate(u.linkExpiresAt)}` : 'Inviteret · link udløbet'}
                            </Badge>
                          )}
                        </span>
                      </td>
                      <td style={{ color: MUTED }}>{u.username}</td>
                      <td>
                        <Select
                          value={u.initials}
                          onChange={e => updateUser(u.username, { initials: e.target.value })}
                        >
                          {!hasSheet && <option value={u.initials}>{u.initials}</option>}
                          {sheetInitials.map(i => <option key={i} value={i}>{i}</option>)}
                        </Select>
                        {!hasSheet && <Badge $tone="warn">Intet ark</Badge>}
                      </td>
                      <td>
                        <Select
                          value={u.role}
                          disabled={isMe}
                          title={isMe ? 'Du kan ikke ændre din egen rolle' : undefined}
                          onChange={e => updateUser(u.username, { role: e.target.value })}
                        >
                          <option value="user">Bruger</option>
                          <option value="admin">Admin</option>
                        </Select>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {confirmDelete === u.username ? (
                          <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ color: MUTED, fontSize: '13px' }}>Slet {u.name}?</span>
                            <Button $small $variant="secondary" onClick={() => setConfirmDelete(null)}>Annullér</Button>
                            <Button $small $variant="danger" onClick={() => deleteUser(u.username)}>Slet</Button>
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', gap: '8px' }}>
                            <Button
                              $small
                              $variant="secondary"
                              title={u.status === 'invited' ? 'Nyt invitationslink' : 'Nulstillingslink – brugeren vælger selv ny kode'}
                              onClick={() => makeLink(u)}
                            >
                              Link
                            </Button>
                            <Button
                              $small
                              $variant="secondary"
                              onClick={() => setResetFor({ username: u.username, password: generatePassword() })}
                            >
                              Ny kode
                            </Button>
                            <Button
                              $small
                              $variant="secondary"
                              disabled={isMe}
                              title={isMe ? 'Du kan ikke slette dig selv' : undefined}
                              onClick={() => setConfirmDelete(u.username)}
                            >
                              Slet
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                    {resetFor?.username === u.username && (
                      <tr>
                        <td colSpan={5} style={{ background: SURFACE, borderRadius: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600 }}>Ny adgangskode til {u.name}:</span>
                            <Input
                              style={{ maxWidth: '260px' }}
                              minLength={MIN_PASSWORD_LENGTH}
                              value={resetFor.password}
                              onChange={e => setResetFor({ ...resetFor, password: e.target.value })}
                            />
                            <Button $small $variant="secondary" onClick={() => setResetFor({ ...resetFor, password: generatePassword() })}>
                              Generér
                            </Button>
                            <Button $small $variant="secondary" onClick={() => setResetFor(null)}>Annullér</Button>
                            <Button $small onClick={saveReset}>Gem kode</Button>
                            {isMe && (
                              <span style={{ color: MUTED, fontSize: '12px' }}>Du bliver logget ud og skal logge ind med den nye kode.</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </Card>
      </Surface>
    </Page>
  );
}

export default Admin;
